import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { useAuth } from './authentication'
import { useWebsocket } from './websocket'
import {
  getActiveScreenShares,
  getScreenShareConfig,
  startScreenShare,
  stopScreenShare,
} from '../api/endpoints'
import type { IceServerConfig, ScreenShareSession, ScreenShareSignal } from '@expostreamer/shared-types'

interface RemoteShare {
  shareId: string
  stream: MediaStream
}

interface ScreenShareContextValue {
  activeShares: ScreenShareSession[]
  // The share this browser is currently broadcasting, if any.
  mySharedId: string | null
  // False on browsers/contexts without getDisplayMedia — iOS Safari
  // (unsupported entirely), older/other Android browsers, or any browser
  // over a non-secure origin (plain http:// on a LAN IP rather than
  // https:// or localhost — the API doesn't exist outside a secure context).
  canShare: boolean
  startSharing: () => Promise<void>
  stopSharing: () => void
  // The share this browser is currently watching, if any — AVPlayer reads
  // `.stream` once `.shareId` matches its VideoSource.url.
  remoteShare: RemoteShare | null
  joinShare: (shareId: string) => void
  leaveShare: (shareId: string) => void
}

const ScreenShareContext = createContext<ScreenShareContextValue | undefined>(undefined)

const canShare = typeof navigator.mediaDevices?.getDisplayMedia === 'function'

function sendSignal(webSocket: WebSocket, signal: ScreenShareSignal): void {
  webSocket.send('share:' + JSON.stringify(signal))
}

export const ScreenShareProvider = ({ children }: { children: ReactNode }) => {
  const { accessToken } = useAuth()
  const { webSocket, registerWebsocketMessaging } = useWebsocket()

  const [activeShares, setActiveShares] = useState<ScreenShareSession[]>([])
  const [mySharedId, setMySharedId] = useState<string | null>(null)
  const [remoteShare, setRemoteShare] = useState<RemoteShare | null>(null)

  const clientIdRef = useRef<string | null>(null)
  // Seeded with the same public STUN fallback the server bakes in, so a
  // RTCPeerConnection created before (or despite) the config fetch below
  // still has something to work with instead of an empty iceServers list.
  const iceServersRef = useRef<IceServerConfig[]>([{ urls: 'stun:stun.l.google.com:19302' }])
  // Resolves once the real config (possibly including a TURN server) has
  // been fetched, or after a timeout if that request stalls — joinShare and
  // handleJoin await this so they never build a RTCPeerConnection with a
  // stale/empty iceServers list, but also never hang forever on a slow
  // network.
  const iceServersReadyRef = useRef<Promise<void>>(Promise.resolve())
  const joinGenerationRef = useRef(0)
  const localStreamRef = useRef<MediaStream | null>(null)
  // Owner side: one RTCPeerConnection per viewer, keyed by the viewer's clientId.
  const viewerPeersRef = useRef<Map<string, RTCPeerConnection>>(new Map())
  // Viewer side: at most one active connection, matching AVPlayer's single
  // VideoSource slot.
  const viewingRef = useRef<{ shareId: string; ownerClientId: string | null; pc: RTCPeerConnection } | null>(
    null
  )
  // ICE candidates gathered before we learn the owner's clientId from their
  // offer can't be addressed yet — held here until then.
  const pendingViewerIceRef = useRef<RTCIceCandidateInit[]>([])

  useEffect(() => {
    if (!accessToken) return
    const fetched = getScreenShareConfig().then(({ ok, data }) => {
      if (ok) iceServersRef.current = data.iceServers
    })
    const timeout = new Promise<void>(resolve => setTimeout(resolve, 3000))
    iceServersReadyRef.current = Promise.race([fetched, timeout])
  }, [accessToken])

  useEffect(() => {
    if (!accessToken) {
      setActiveShares([])
      return
    }
    const refresh = () => {
      getActiveScreenShares().then(({ ok, data }) => {
        if (ok) setActiveShares(data)
      })
    }
    refresh()
    const interval = setInterval(refresh, 5000)
    return () => clearInterval(interval)
  }, [accessToken])

  // Memoized so AVPlayer/Drawer effects that call these on VideoSource
  // changes don't see a new function identity (and re-fire) on every
  // unrelated provider re-render, e.g. the 5s activeShares poll.
  const closeViewingPeer = useCallback(() => {
    joinGenerationRef.current++ // invalidate any in-flight joinShare() call
    viewingRef.current?.pc.close()
    viewingRef.current = null
    pendingViewerIceRef.current = []
    setRemoteShare(null)
  }, [])

  const stopSharing = useCallback(() => {
    localStreamRef.current?.getTracks().forEach(track => track.stop())
    localStreamRef.current = null
    for (const pc of viewerPeersRef.current.values()) pc.close()
    viewerPeersRef.current.clear()

    setMySharedId(current => {
      if (current) stopScreenShare(current).then(({ ok, data }) => ok && setActiveShares(data))
      return null
    })
  }, [])

  const leaveShare = useCallback(
    (shareId: string) => {
      if (viewingRef.current?.shareId !== shareId) return
      if (viewingRef.current.ownerClientId) {
        sendSignal(webSocket, { kind: 'leave', shareId, to: viewingRef.current.ownerClientId })
      }
      closeViewingPeer()
    },
    [webSocket, closeViewingPeer]
  )

  const joinShare = useCallback(
    (shareId: string) => {
      if (viewingRef.current) closeViewingPeer()
      const generation = ++joinGenerationRef.current

      iceServersReadyRef.current.finally(() => {
        // A later joinShare()/leaveShare() call superseded this one while we
        // were waiting — don't build a connection nothing will use.
        if (joinGenerationRef.current !== generation) return

        const pc = new RTCPeerConnection({ iceServers: iceServersRef.current })
        viewingRef.current = { shareId, ownerClientId: null, pc }

        pc.ontrack = event => {
          setRemoteShare({ shareId, stream: event.streams[0] })
        }
        pc.onicecandidate = event => {
          if (!event.candidate) return
          const ownerClientId = viewingRef.current?.ownerClientId
          if (ownerClientId) {
            sendSignal(webSocket, {
              kind: 'ice',
              shareId,
              to: ownerClientId,
              candidate: event.candidate.toJSON(),
            })
          } else {
            pendingViewerIceRef.current.push(event.candidate.toJSON())
          }
        }

        sendSignal(webSocket, { kind: 'join', shareId })
      })
    },
    [webSocket, closeViewingPeer]
  )

  const startSharing = useCallback(async () => {
    const clientId = clientIdRef.current
    if (!clientId || !canShare) return

    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true })
    const { ok, data } = await startScreenShare(clientId)
    if (!ok) {
      stream.getTracks().forEach(track => track.stop())
      return
    }

    localStreamRef.current = stream
    setMySharedId(data.shareId)
    stream.getVideoTracks()[0]?.addEventListener('ended', stopSharing)

    getActiveScreenShares().then(({ ok, data }) => ok && setActiveShares(data))
  }, [stopSharing])

  // Signal handling lives inside the effect (rather than as component-body
  // functions) so it always closes over the current `mySharedId` without
  // needing every helper it calls listed as an effect dependency.
  useEffect(() => {
    async function handleJoin(shareId: string, viewerClientId: string) {
      if (shareId !== mySharedId || !localStreamRef.current) return
      await iceServersReadyRef.current

      const pc = new RTCPeerConnection({ iceServers: iceServersRef.current })
      viewerPeersRef.current.set(viewerClientId, pc)
      for (const track of localStreamRef.current.getTracks()) pc.addTrack(track, localStreamRef.current)

      pc.onicecandidate = event => {
        if (!event.candidate) return
        sendSignal(webSocket, {
          kind: 'ice',
          shareId,
          to: viewerClientId,
          candidate: event.candidate.toJSON(),
        })
      }
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'closed' || pc.connectionState === 'failed') {
          viewerPeersRef.current.delete(viewerClientId)
        }
      }

      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)
      sendSignal(webSocket, { kind: 'offer', shareId, to: viewerClientId, sdp: offer })
    }

    async function handleOffer(shareId: string, ownerClientId: string, sdp: RTCSessionDescriptionInit) {
      if (viewingRef.current?.shareId !== shareId) return
      const pc = viewingRef.current.pc

      await pc.setRemoteDescription(sdp)
      viewingRef.current.ownerClientId = ownerClientId
      for (const candidate of pendingViewerIceRef.current) {
        sendSignal(webSocket, { kind: 'ice', shareId, to: ownerClientId, candidate })
      }
      pendingViewerIceRef.current = []

      const answer = await pc.createAnswer()
      await pc.setLocalDescription(answer)
      sendSignal(webSocket, { kind: 'answer', shareId, to: ownerClientId, sdp: answer })
    }

    async function handleAnswer(viewerClientId: string, sdp: RTCSessionDescriptionInit) {
      await viewerPeersRef.current.get(viewerClientId)?.setRemoteDescription(sdp)
    }

    async function handleIce(fromClientId: string, candidate: RTCIceCandidateInit) {
      if (viewingRef.current?.ownerClientId === fromClientId) {
        await viewingRef.current.pc.addIceCandidate(candidate)
        return
      }
      await viewerPeersRef.current.get(fromClientId)?.addIceCandidate(candidate)
    }

    function handleEnded(shareId: string) {
      if (viewingRef.current?.shareId !== shareId) return
      viewingRef.current.pc.close()
      viewingRef.current = null
      pendingViewerIceRef.current = []
      setRemoteShare(null)
    }

    const unregister = registerWebsocketMessaging(message => {
      if (typeof message.data !== 'string') return

      if (message.data.startsWith('clientid:')) {
        clientIdRef.current = message.data.slice('clientid:'.length)
        return
      }
      if (!message.data.startsWith('share:')) return

      const signal = JSON.parse(message.data.slice('share:'.length)) as ScreenShareSignal
      switch (signal.kind) {
        case 'join':
          if (signal.from) handleJoin(signal.shareId, signal.from)
          break
        case 'offer':
          if (signal.from) handleOffer(signal.shareId, signal.from, signal.sdp as RTCSessionDescriptionInit)
          break
        case 'answer':
          if (signal.from) handleAnswer(signal.from, signal.sdp as RTCSessionDescriptionInit)
          break
        case 'ice':
          if (signal.from) handleIce(signal.from, signal.candidate as RTCIceCandidateInit)
          break
        case 'ended':
          handleEnded(signal.shareId)
          // The share may have been force-stopped (e.g. by an admin) from
          // elsewhere — if it was mine, tear down the owning side too.
          if (signal.shareId === mySharedId) stopSharing()
          break
      }
    })
    return unregister
  }, [registerWebsocketMessaging, webSocket, mySharedId, stopSharing])

  return (
    <ScreenShareContext
      value={{
        activeShares,
        mySharedId,
        canShare,
        startSharing,
        stopSharing,
        remoteShare,
        joinShare,
        leaveShare,
      }}
    >
      {children}
    </ScreenShareContext>
  )
}

export const useScreenShare = (): ScreenShareContextValue => {
  const context = useContext(ScreenShareContext)
  if (!context) {
    throw new Error('useScreenShare must be used within a ScreenShareProvider')
  }
  return context
}
